/**
 * Super-admin v2 — CafeDetailModal.
 * Header + stats + owner + subscription + actions + Kaspi B2B key + 3D models.
 */
import { useEffect, useState, type ReactNode } from "react";
import { X, MapPin, KeyRound, Box, Play, Pause, Trash2 } from "lucide-react";
import {
  superAdminGetTenantFull,
  superAdminSuspendTenant,
  superAdminUnsuspendTenant,
  superAdminDeleteTenant,
  superAdminRequestDeleteToken,
  superAdminChangeTenantPlan,
  superAdminEnsureSubscription,
  superAdminListPlans,
  type TenantFullSnapshot,
  type PlanRowFull,
  type SubscriptionStatusValue,
} from "@cafe-os/db";
import { C, FONT } from "../styles/theme";
import { fmtMoney } from "../lib/money";
import { ModalWrapper, Avatar } from "./ui";
import { PaymentConfigBlock } from "./PaymentConfigBlock";
import { TariffRequestCard } from "./TariffRequestCard";
import type { CafeListRow } from "../screens/CafesScreen";

interface Props {
  cafe: CafeListRow;
  onClose: () => void;
  onResetPassword: (staffId: string) => void;
  onActionDone?: () => void;
  /** SA-6: открыть полноэкранную карточку тенанта (3D-модели меню). */
  onOpenTenantDetail?: (slug: string) => void;
}

export function CafeDetailModal({ cafe, onClose, onResetPassword, onActionDone, onOpenTenantDetail }: Props) {
  const [snap, setSnap] = useState<TenantFullSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanRowFull[]>([]);
  const [planDraft, setPlanDraft] = useState<string>("");
  const [statusDraft, setStatusDraft] = useState<SubscriptionStatusValue | "">("");
  const [planResetPeriod, setPlanResetPeriod] = useState(false);
  const [periodMonthsDraft, setPeriodMonthsDraft] = useState<number>(1);
  const [planBusy, setPlanBusy] = useState(false);
  // SA-2: назначение плана кафе БЕЗ подписки (раньше блок плана просто
  // не рендерился и биллинг был недостижим из UI)
  const [assignPlanId, setAssignPlanId] = useState<string>("starter");
  const [assignTrialDays, setAssignTrialDays] = useState<number>(14);

  const PERIOD_OPTIONS: Array<{ months: number; label: string }> = [
    { months: 1,  label: "1 месяц" },
    { months: 3,  label: "3 месяца" },
    { months: 6,  label: "6 месяцев" },
    { months: 12, label: "1 год" },
    { months: 24, label: "2 года" },
  ];

  const STATUS_OPTIONS: Array<{ value: SubscriptionStatusValue; label: string }> = [
    { value: "trialing",  label: "Trial (пробный)" },
    { value: "active",    label: "Активна (оплачено)" },
    { value: "past_due",  label: "Просрочка" },
    { value: "blocked",   label: "Заблокирована" },
    { value: "cancelled", label: "Отменена" },
  ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await superAdminGetTenantFull(cafe.id);
        if (cancelled) return;
        setSnap(s);
        if (s.subscription?.plan_id) setPlanDraft(s.subscription.plan_id);
        if (s.subscription?.status) {
          setStatusDraft(s.subscription.status as SubscriptionStatusValue);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
        if (!cancelled) setLoading(false);
        return;
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Best-effort: список планов для селекта тарифа. Владелец теперь приходит
      // в snap.owner из super_admin_get_tenant_full (00189) — прямой select из
      // staff не работал, т.к. super_admin не член тенанта и его режет RLS.
      void superAdminListPlans()
        .then((list) => { if (!cancelled) setPlans(list); })
        .catch((e) => {
          console.warn("[CafeDetailModal] superAdminListPlans failed:", e);
        });
    })();
    return () => { cancelled = true; };
  }, [cafe.id]);

  async function reloadSnapshot() {
    const s = await superAdminGetTenantFull(cafe.id);
    setSnap(s);
    if (s.subscription?.plan_id) setPlanDraft(s.subscription.plan_id);
    if (s.subscription?.status) {
      setStatusDraft(s.subscription.status as SubscriptionStatusValue);
    }
  }

  async function handleChangePlan() {
    if (!snap?.subscription) return;
    const currentPlanId = snap.subscription.plan_id;
    const currentStatus = snap.subscription.status as SubscriptionStatusValue;
    const planChanged = planDraft !== currentPlanId;
    const statusChanged = statusDraft !== "" && statusDraft !== currentStatus;
    if (!planChanged && !statusChanged && !planResetPeriod) {
      setToast("Ничего не изменилось");
      return;
    }

    const targetPlan = plans.find((p) => p.id === planDraft);
    const targetPlanName = targetPlan?.name?.ru ?? planDraft;
    const targetStatusLabel = STATUS_OPTIONS.find((s) => s.value === statusDraft)?.label ?? statusDraft;
    const targetPeriodLabel = PERIOD_OPTIONS.find((p) => p.months === periodMonthsDraft)?.label
      ?? `${periodMonthsDraft} мес`;

    const lines: string[] = [];
    if (planChanged) lines.push(`План: ${currentPlanId} → «${targetPlanName}»`);
    if (statusChanged) lines.push(`Статус: ${currentStatus} → «${targetStatusLabel}»`);
    if (planResetPeriod) lines.push(`Период: новый отсчёт на ${targetPeriodLabel} (с сегодня)`);

    if (!window.confirm(`Применить изменения?\n\n${lines.join("\n")}`)) return;

    setPlanBusy(true);
    try {
      const res = await superAdminChangeTenantPlan({
        tenantId: cafe.id,
        newPlanId: planDraft,
        resetPeriod: planResetPeriod,
        periodMonths: planResetPeriod ? periodMonthsDraft : null,
        newStatus: statusChanged ? (statusDraft as SubscriptionStatusValue) : null,
      });
      if (!res.changed) {
        setToast("Без изменений");
      } else {
        const parts: string[] = [];
        if (res.old_plan_id !== res.new_plan_id) parts.push(`план: ${res.old_plan_id} → ${res.new_plan_id}`);
        if (res.status_changed) parts.push(`статус: ${res.old_status} → ${res.new_status}`);
        if (res.period_reset) parts.push(`период: ${res.period_months} мес от сегодня`);
        setToast(parts.length ? `Готово — ${parts.join(", ")}` : "Готово");
      }
      await reloadSnapshot();
      onActionDone?.();
    } catch (e) {
      setToast(`Ошибка: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPlanBusy(false);
    }
  }

  async function handleSuspend() {
    const reason = window.prompt("Причина приостановки:");
    if (!reason || reason.trim().length === 0) return;
    setBusy(true);
    try {
      const res = await superAdminSuspendTenant(cafe.id, reason.trim());
      setToast(`Приостановлено. Закрыто сессий: ${res.closed_sessions}`);
      onActionDone?.();
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setToast(`Ошибка: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleUnsuspend() {
    setBusy(true);
    try {
      await superAdminUnsuspendTenant(cafe.id);
      setToast("Активировано");
      onActionDone?.();
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setToast(`Ошибка: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    const phrase = window.prompt(
      `⚠️ УДАЛЕНИЕ кафе ${cafe.id} необратимо.\n\nДля подтверждения введи: DELETE-${cafe.id}`,
    );
    if (!phrase || phrase.trim() !== `DELETE-${cafe.id}`) {
      setToast("Удаление отменено");
      return;
    }
    setBusy(true);
    try {
      // фраза — только UX-защита от случайного клика; настоящий контроль —
      // одноразовый серверный токен (audit 2026-07-04 #5), невыводимый и short-lived.
      const { token } = await superAdminRequestDeleteToken(cafe.id);
      await superAdminDeleteTenant(cafe.id, token);
      setToast("Кафе удалено");
      onActionDone?.();
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setToast(`Ошибка: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const isSuspended = snap?.tenant?.is_active === false;

  return (
    <ModalWrapper onClose={onClose} wide>
      <div style={{ padding: 32 }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16, marginBottom: 24,
          paddingBottom: 20, borderBottom: `1px solid ${C.border}`,
        }}>
          <Avatar text={cafe.name[0] ?? "?"} size={60} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: FONT.display, fontSize: 24, fontWeight: 700, color: C.text,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              {cafe.name}
              {isSuspended && (
                <span style={{
                  background: C.redSoft, color: C.red,
                  fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 800,
                }}>SUSPENDED</span>
              )}
            </div>
            <div style={{
              fontSize: 13, color: C.muted, marginTop: 4,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <MapPin size={13} /> {cafe.id} · {cafe.plan} · {fmtDate(cafe.createdAt)}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: C.bgSoft, color: C.text, border: "none",
            width: 36, height: 36, borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><X size={16} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Загрузка...</div>
        ) : err ? (
          <div style={{ padding: 20, color: C.red }}>{err}</div>
        ) : snap ? (
          <>
            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: 24 }}>
              <StatCell label="Сегодня" value={`${snap.stats.today_revenue.toLocaleString("ru-RU")} ₸`} color={C.green} />
              <StatCell label="Заказов" value={snap.stats.today_orders} color={C.blue} />
              <StatCell label="Персонал" value={snap.stats.staff_count} color={C.brand} />
              <StatCell label="Активность" value={snap.stats.last_order_at ? fmtAgo(snap.stats.last_order_at) : "—"} color={C.text} small />
            </div>

            {/* Owner block */}
            <SectionLabel>Владелец</SectionLabel>
            <div style={{
              background: C.bgSoft, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 16, marginBottom: 20,
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: C.brand, color: "#0A0E14",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 800, fontFamily: FONT.display,
              }}>{(snap.owner?.display_name || cafe.name)[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  {snap.owner?.display_name ?? "—"}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontFamily: FONT.mono }}>
                  {snap.owner?.phone ?? cafe.phone ?? "Нет телефона"}
                </div>
              </div>
              {snap.owner?.id && (
                <button onClick={() => onResetPassword(snap.owner!.id)} style={{
                  background: C.orangeSoft, color: C.orange,
                  border: `1px solid ${C.orange}50`,
                  padding: "8px 14px", borderRadius: 10,
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}><KeyRound size={14} /> Сбросить пароль/PIN</button>
              )}
            </div>

            {/* Subscription block */}
            {snap.subscription && snap.plan && (
              <>
                <SectionLabel>Подписка</SectionLabel>
                <div style={{
                  background: C.bgSoft, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 16, marginBottom: 12,
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12,
                }}>
                  <SubField label="План" value={snap.plan.name?.ru ?? snap.subscription.plan_id} brand />
                  <SubField label="Цена" value={`${fmtMoney(snap.plan.monthly_price, snap.plan.currency)}/мес`} />
                  <SubField label="Статус" value={statusLabel(snap.subscription.status)} />
                  <SubField label="След. платёж" value={snap.subscription.current_period_end ? fmtDate(snap.subscription.current_period_end) : "—"} />
                </div>

                {/* Запрос кафе на свой тариф из конструктора (00458) — виден только при pending */}
                <TariffRequestCard
                  tenantId={cafe.id}
                  onApplied={() => { void reloadSnapshot(); onActionDone?.(); }}
                />

                {/* Управление подпиской */}
                <div style={{
                  background: C.bgSoft, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 16, marginBottom: 20,
                  display: "grid", gap: 14,
                }}>
                  <div style={{
                    fontSize: 11, color: C.muted, letterSpacing: 1,
                    textTransform: "uppercase", fontWeight: 700,
                  }}>Управление подпиской</div>

                  {/* Plan select */}
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>Тариф</div>
                    <select
                      value={planDraft}
                      onChange={(e) => setPlanDraft(e.target.value)}
                      disabled={planBusy || plans.length === 0}
                      style={selectFieldStyle}
                    >
                      {plans
                        .filter((p) => p.is_active || p.id === snap.subscription?.plan_id)
                        .map((p) => {
                          const name = p.name?.ru ?? p.name?.en ?? p.name?.kz ?? p.id;
                          const priceLabel = fmtMoney(p.monthly_price, p.currency);
                          const isCurrent = p.id === snap.subscription?.plan_id;
                          return (
                            <option key={p.id} value={p.id}>
                              {name} — {priceLabel}/мес{isCurrent ? "  (текущий)" : ""}{!p.is_active ? "  [неактивен]" : ""}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  {/* Status select */}
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>Статус</div>
                    <select
                      value={statusDraft}
                      onChange={(e) => setStatusDraft(e.target.value as SubscriptionStatusValue)}
                      disabled={planBusy}
                      style={selectFieldStyle}
                    >
                      {STATUS_OPTIONS.map((s) => {
                        const isCurrent = s.value === snap.subscription?.status;
                        return (
                          <option key={s.value} value={s.value}>
                            {s.label}{isCurrent ? "  (текущий)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>
                      Переключение в «Активна» очистит trial_ends_at / cancelled_at.
                    </div>
                  </div>

                  {/* Reset period + duration */}
                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={planResetPeriod}
                        onChange={(e) => setPlanResetPeriod(e.target.checked)}
                        disabled={planBusy}
                      />
                      Перезапустить billing-период (новый отсчёт с сегодня)
                    </label>
                    {planResetPeriod && (
                      <div style={{ display: "grid", gap: 6, paddingLeft: 24 }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>На сколько</div>
                        <select
                          value={periodMonthsDraft}
                          onChange={(e) => setPeriodMonthsDraft(parseInt(e.target.value, 10))}
                          disabled={planBusy}
                          style={selectFieldStyle}
                        >
                          {PERIOD_OPTIONS.map((opt) => (
                            <option key={opt.months} value={opt.months}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Apply button */}
                  <button
                    onClick={() => void handleChangePlan()}
                    disabled={
                      planBusy ||
                      (
                        planDraft === snap.subscription.plan_id &&
                        statusDraft === snap.subscription.status &&
                        !planResetPeriod
                      )
                    }
                    style={{
                      background: C.brand, color: "#0A0E14",
                      border: "none", borderRadius: 8,
                      padding: "11px 18px", fontSize: 13, fontWeight: 800,
                      cursor: planBusy ? "wait" : "pointer",
                      opacity:
                        planBusy ||
                        (
                          planDraft === snap.subscription.plan_id &&
                          statusDraft === snap.subscription.status &&
                          !planResetPeriod
                        ) ? 0.5 : 1,
                    }}
                  >{planBusy ? "Применяю…" : "Применить"}</button>
                </div>
              </>
            )}

            {/* SA-2: кафе без подписки — блок назначения плана (раньше блок
                плана просто не рендерился → биллинг недостижим из UI) */}
            {snap && !snap.subscription && (
              <>
                <SectionLabel>Подписка</SectionLabel>
                <div style={{
                  background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 12, padding: 16, marginBottom: 20,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#ef4444", marginBottom: 4 }}>
                    У кафе нет подписки
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                    Биллинг-цикл его не видит: счета не выставляются, в MRR не попадает.
                    Назначь план — подписка стартует со статусом trialing.
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <select
                      value={assignPlanId}
                      onChange={(e) => setAssignPlanId(e.target.value)}
                      style={{
                        background: C.bgCard, color: C.text, border: `1px solid ${C.border}`,
                        borderRadius: 8, padding: "9px 10px", fontSize: 13,
                      }}
                    >
                      {plans.filter((p) => p.is_active).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name?.ru ?? p.id} — {fmtMoney(p.monthly_price, p.currency)}/мес
                        </option>
                      ))}
                    </select>
                    {[7, 14, 30].map((d) => (
                      <button key={d} onClick={() => setAssignTrialDays(d)} style={{
                        background: assignTrialDays === d ? C.brandSoft : C.bgCard,
                        color: assignTrialDays === d ? C.brand : C.text,
                        border: `1px solid ${assignTrialDays === d ? C.brand : C.border}`,
                        borderRadius: 8, padding: "9px 12px", fontSize: 12, fontWeight: 700,
                        cursor: "pointer",
                      }}>trial {d} дн</button>
                    ))}
                    <button
                      onClick={() => {
                        void (async () => {
                          setPlanBusy(true);
                          try {
                            const res = await superAdminEnsureSubscription(cafe.id, assignPlanId, assignTrialDays);
                            setToast(`Подписка создана: ${res.plan_id} (${res.status})`);
                            await reloadSnapshot();
                            onActionDone?.();
                          } catch (e) {
                            setToast(`Ошибка: ${e instanceof Error ? e.message : String(e)}`);
                          } finally {
                            setPlanBusy(false);
                          }
                        })();
                      }}
                      disabled={planBusy || plans.length === 0}
                      style={{
                        background: C.brand, color: "#0A0E14", border: "none",
                        borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 800,
                        cursor: planBusy ? "wait" : "pointer", opacity: planBusy ? 0.5 : 1,
                      }}
                    >{planBusy ? "Назначаю…" : "Назначить план"}</button>
                  </div>
                </div>
              </>
            )}

            {/* SA-6: 3D-модели меню — эксклюзив платформы (загрузчик жил в
                неподключённом TenantDetailScreen) */}
            {onOpenTenantDetail && (
              <button
                onClick={() => onOpenTenantDetail(cafe.id)}
                style={{
                  background: C.bgSoft, color: C.text, border: `1px solid ${C.border}`,
                  borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", marginBottom: 20, width: "100%", textAlign: "left",
                  display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <Box size={16} /> 3D-модели меню (загрузка .glb) →
              </button>
            )}

            {/* Платформенный Kaspi B2B API-ключ. Способ оплаты (касса / Kaspi-перевод /
                удалёнка) владелец кафе настраивает сам в своём кабинете. */}
            <div style={{ marginBottom: 20 }}>
              <PaymentConfigBlock tenantSlug={cafe.id} />
            </div>

            {/* Invoices summary */}
            <SectionLabel>Счета</SectionLabel>
            <div style={{
              background: C.bgSoft, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 16, marginBottom: 20,
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 12, fontSize: 12,
            }}>
              <SubField label="Всего" value={String(snap.invoices_summary.total)} />
              <SubField label="Оплачено" value={String(snap.invoices_summary.paid)} brand />
              <SubField label="Ожидают" value={String(snap.invoices_summary.pending)} />
            </div>

            {/* Actions */}
            <SectionLabel>Действия</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
              {isSuspended ? (
                <ActionBtn icon={<Play size={16} />} label="Активировать" color={C.green} onClick={() => void handleUnsuspend()} disabled={busy} />
              ) : (
                <ActionBtn icon={<Pause size={16} />} label="Приостановить" color={C.orange} onClick={() => void handleSuspend()} disabled={busy} />
              )}
              <ActionBtn icon={<Trash2 size={16} />} label="Удалить кафе" color={C.red} onClick={() => void handleDelete()} disabled={busy} />
            </div>

            {toast && (
              <div style={{
                marginTop: 16, padding: 12,
                background: C.bgSoft, border: `1px solid ${C.border}`,
                borderRadius: 10, color: C.text, fontSize: 13,
              }}>
                {toast}
              </div>
            )}
          </>
        ) : null}
      </div>
    </ModalWrapper>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 11, color: C.muted, letterSpacing: 1,
      textTransform: "uppercase", fontWeight: 700, marginBottom: 10,
    }}>{children}</div>
  );
}

function StatCell({ label, value, color, small }: {
  label: string; value: string | number; color: string; small?: boolean;
}) {
  return (
    <div style={{ background: C.bgSoft, borderRadius: 10, padding: 12 }}>
      <div style={{
        fontSize: 10, color: C.muted, letterSpacing: 1,
        textTransform: "uppercase", fontWeight: 700,
      }}>{label}</div>
      <div style={{
        fontFamily: FONT.display, fontSize: small ? 14 : 20, fontWeight: 700, color, marginTop: 4,
      }}>{value}</div>
    </div>
  );
}

function SubField({ label, value, brand }: { label: string; value: string; brand?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: brand ? C.brand : C.text }}>{value}</div>
    </div>
  );
}

const selectFieldStyle: React.CSSProperties = {
  background: C.bg,
  color: C.text,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  fontWeight: 600,
  outline: "none",
  cursor: "pointer",
  width: "100%",
  boxSizing: "border-box",
};

function ActionBtn({ icon, label, color, onClick, disabled }: {
  icon: ReactNode; label: string; color: string; onClick: () => void; disabled?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: C.bgSoft, color,
      border: `1px solid ${color}40`,
      padding: "12px", borderRadius: 10,
      fontSize: 12, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      display: "flex", alignItems: "center", gap: 8, textAlign: "left",
    }}>
      <span style={{ display: "flex", alignItems: "center" }}>{icon}</span>{label}
    </button>
  );
}

function statusLabel(s: string): string {
  return s === "active" ? "Активна"
    : s === "trialing" ? "Trial"
    : s === "past_due" ? "Просрочка"
    : s === "blocked" ? "Заблокирована"
    : s === "cancelled" ? "Отменена"
    : s;
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return iso; }
}

function fmtAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "сейчас";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}м`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}ч`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}д`;
  return fmtDate(iso);
}
